import { useCallback } from 'react';

import { ConversationListItemContent } from '@apps/web/components/shared/messages/conversation_list_item_content';
import {
    ContextMenu,
    ContextMenuContent,
    ContextMenuItem,
    ContextMenuTrigger,
} from '@apps/web/components/ui/context_menu';
import { useOrganizationTimezone } from '@apps/web/context/timezone_provider';
import { toast } from '@apps/web/lib/app_notifications';
import { cn } from '@apps/web/lib/utils';
import { type OrganizationConversationMetadata } from '@libraries/typescript/api_client';

type ConversationListRowProps = {
    readonly conversation: OrganizationConversationMetadata;
    readonly selected: boolean;
    readonly onSelect: () => void;
    readonly markAsRead: (conversationId: string) => Promise<void>;
    readonly markAsUnread: (conversationId: string) => Promise<void>;
    readonly showOwnership?: boolean;
    readonly typingUsers?: ReadonlyArray<{ readonly displayName: string }>;
};

export function ConversationListRow({
    conversation,
    selected,
    onSelect,
    markAsRead,
    markAsUnread,
    showOwnership = false,
    typingUsers,
}: ConversationListRowProps) {
    const { timezone } = useOrganizationTimezone();
    const isUnread = conversation.unread_count > 0;

    const handleMarkRead = useCallback(async () => {
        try {
            await markAsRead(conversation.id);
            toast.success('Marked as read', { persistNotification: false });
        } catch (error) {
            console.error('Failed to mark conversation as read', error);
            toast.error('Failed to mark as read', {
                persistNotification: false,
            });
        }
    }, [conversation.id, markAsRead]);

    const handleMarkUnread = useCallback(async () => {
        try {
            await markAsUnread(conversation.id);
            toast.success('Marked as unread', { persistNotification: false });
        } catch (error) {
            console.error('Failed to mark conversation as unread', error);
            toast.error('Failed to mark as unread', {
                persistNotification: false,
            });
        }
    }, [conversation.id, markAsUnread]);

    return (
        <ContextMenu>
            <ContextMenuTrigger asChild={true}>
                <button
                    className={cn(
                        'w-full cursor-pointer rounded-lg border px-2.5 py-2 text-left transition-colors',
                        selected
                            ? 'border-border bg-accent'
                            : 'border-transparent hover:bg-accent/80',
                    )}
                    onClick={onSelect}
                    type="button"
                >
                    <ConversationListItemContent
                        conversation={conversation}
                        showOwnership={showOwnership}
                        showPendingReviewIndicator={true}
                        timezone={timezone}
                        typingUsers={typingUsers}
                    />
                </button>
            </ContextMenuTrigger>
            <ContextMenuContent>
                {isUnread ? (
                    <ContextMenuItem onSelect={() => void handleMarkRead()}>
                        Mark as read
                    </ContextMenuItem>
                ) : (
                    <ContextMenuItem onSelect={() => void handleMarkUnread()}>
                        Mark as unread
                    </ContextMenuItem>
                )}
            </ContextMenuContent>
        </ContextMenu>
    );
}
